<template>
  <v-container fluid>
    <v-row justify="space-between" class="px-10">
      <v-col cols="12" sm="12" md="4">
        <p class="text-h6 text--secondary font-weight-bold">Pre-Sets</p>
      </v-col>
      <v-col cols="12" sm="12" md="3">
        <v-btn text color="secondary" @click="changeRouteFilters()">
          <v-icon left dark color="primary"> mdi-cog </v-icon>
          Filters
        </v-btn>
        <v-btn text color="secondary" @click="createPreSet()">
          <v-icon left dark color="primary"> mdi-plus-circle </v-icon>
          New Pre-Set
        </v-btn>
      </v-col>
      <v-col cols="12" sm="12" md="5">
        <v-text-field
          label="Search for a Pre-Set"
          v-model="search"
          single-line
          outlined
          rounded
          filled
          dense
          append-icon="mdi-magnify"
        ></v-text-field>
      </v-col>

      <v-col cols="12">
        <v-data-table
          :search="search"
          :loading="loading"
          :headers="headers"
          :items="getPreSets"
          sort-by="name.ENG"
          item-key="id"
          fixed-header
          class="elevation-4"
          :page.sync="page"
          @update:page="onPageChange"
          :items-per-page.sync="itemsPerPage"
          @update:items-per-page="onItemsPerPageUpdate"
          :sortBy.sync="sortBy"
          @update:sortBy="onSortByChange"
        >
          <template v-slot:[`item.image`]="{ item }">
            <v-tooltip top color="secondary">
              <template v-slot:activator="{ on, attrs }">
                <v-avatar class="my-2">
                  <img
                    v-if="item.image.link"
                    v-bind="attrs"
                    v-on="on"
                    style="cursor: pointer"
                    @click="openDialogPreviewImage(item.image.link)"
                    :src="item.image.link"
                  />
                </v-avatar>
              </template>
              <span>Show Image</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.description.ENG`]="{ item }">
            <div
              v-if="!editingDescription[item.id]"
              class="descriptionSlot editable-field"
              @click="startEditingDescription(item)"
            >
              {{ item.description.ENG }}
            </div>
            <v-textarea
              v-else
              v-model="editingDescription[item.id]"
              @keyup.enter="handleEnterKey($event, item, 'description')"
              @keyup.esc="cancelEditingDescription(item)"
              @blur="cancelEditingDescription(item)"
              dense
              outlined
              hide-details
              class="editable-input"
              auto-grow
              rows="2"
              ref="descriptionInput"
            ></v-textarea>
          </template>
          <template v-slot:[`item.name.ENG`]="{ item }">
            <div
              v-if="!editingName[item.id]"
              class="nameSlot editable-field"
              @click="startEditingName(item)"
            >
              {{ item.name.ENG }}
            </div>
            <v-text-field
              v-else
              v-model="editingName[item.id]"
              @keyup.enter="handleEnterKey($event, item, 'name')"
              @keyup.esc="cancelEditingName(item)"
              @blur="cancelEditingName(item)"
              dense
              outlined
              hide-details
              class="editable-input"
              ref="nameInput"
            ></v-text-field>
          </template>
          <template v-slot:[`item.dynamicLink`]="{ item }">
            <!-- <v-btn icon color="primary" @click="openDialogDynamicLink(item)">
              <v-icon>mdi-link</v-icon>
            </v-btn> -->
            <v-btn icon color="primary" @click="copyLink(item.newDeepLink)">
              <v-icon>mdi-plus-box-multiple</v-icon>
            </v-btn>
          </template>
          <template v-slot:[`item.typeBy`]="{ item }">
            {{ item.typeBy === "circuit" ? "By Circuit" : "By Station" }}
          </template>
          <template v-slot:[`item.circuits`]="{ item }">
            {{ item.typeBy === "circuit" ? item.circuits.length : 1 }}
          </template>
          <template v-slot:[`item.enabled`]="{ item }">
            <v-switch
              v-if="item"
              color="primary"
              class="center-switch"
              :input-value="item.enabled"
              @click="updateValue(item, 'enabled', !item.enabled)"
              flat
            ></v-switch>
          </template>
          <template v-slot:[`item.isFree`]="{ item }">
            <v-switch
              v-if="item"
              color="primary"
              class="center-switch"
              :input-value="item.isFree"
              @click="updateValue(item, 'isFree', !item.isFree)"
              flat
            ></v-switch>
          </template>
          <template v-slot:[`item.isGrandFathered`]="{ item }">
            <v-switch
              v-if="item"
              color="primary"
              class="center-switch"
              :input-value="item.isGrandFathered"
              @click="
                updateValue(item, 'isGrandFathered', !item.isGrandFathered)
              "
              flat
            ></v-switch>
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <options-button
              :onEdit="
                () => {
                  changeRoutePreSet(item);
                }
              "
              :onDuplicate="
                () => {
                  duplicatePreSet(item);
                }
              "
              :onDelete="
                () => {
                  openDialogConfirm(item);
                }
              "
              :itemId="item.id"
              :key="item.id"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog.show" width="300" class="rounded-xl">
      <v-card class="text-center">
        <v-icon class="text-center ma-2" size="120" color="primary"
          >mdi-alert</v-icon
        >
        <v-card-text class="pa-4 text-center"
          >This action is irreversible and will delete all data about the
          Pre-Set. Including the image, circuits, and information</v-card-text
        >
        <v-card-text class="text-center">Are you sure?</v-card-text>

        <v-btn
          color="third"
          tile
          width="150"
          class="pa-0 text--secondary"
          @click="closeDialogConfirm()"
          >No</v-btn
        >

        <v-btn
          color="primary"
          tile
          width="150"
          class="pa-0"
          @click="openDialogReAuth('deletePreSet', dialog.item)"
          >Yes</v-btn
        >
        <!-- </v-card-actions> -->
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogPreviewImage.show" max-width="800">
      <v-card style="line-height: 0; !important;">
        <v-btn
          fab
          x-small
          color="red"
          style="position: absolute; top: 5px; left: 5px"
          @click="closeDialogImagePreview()"
        >
          <v-icon size="15" color="white">mdi-close</v-icon>
        </v-btn>
        <img :src="dialogPreviewImage.img" style="width: 100%; height: 100%" />
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar.snackbar"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      >{{ snackbar.text }}</v-snackbar
    >
    <modal-reauth
      :dialogReAuth="dialogReAuth"
      @isSuccessReAuth="isSuccessReAuth"
    ></modal-reauth>
    <v-dialog
      v-model="dialogDynamicLink.show"
      transition="dialog-bottom-transition"
      max-width="700"
    >
      <v-card class="text-center">
        <v-card-actions class="pa-0">
          <v-btn
            icon
            color="secondary"
            class="ml-3 mt-2"
            @click="closeDialogDynamicLink()"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-card-text class="pb-8 mt-3">
          <div class="text-center d-flex align-center mt-5">
            <template
              v-if="
                dialogDynamicLink.item &&
                dialogDynamicLink.item.dynamicLink &&
                dialogDynamicLink.item.dynamicLink.length > 0
              "
            >
              <v-text-field
                v-model="dialogDynamicLink.item.dynamicLink"
                ref="proTrainDynamicLinkRef"
                hide-details="auto"
                maxlength="4"
                class="centered-input"
                readonly
                solo
                flat
                background-color="#efefef"
              ></v-text-field>
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="primary"
                    @click="copyLink(dialogDynamicLink.item.dynamicLink)"
                  >
                    <v-icon>mdi-plus-box-multiple</v-icon>
                  </v-btn>
                </template>
                <span>Copy to clipboard</span>
              </v-tooltip>
            </template>
            <template v-else>
              <p class="secondary--text subtitle-1 mx-auto">
                There is no dynamic link for this workout yet
              </p>
            </template>
          </div>
          <div class="text-center mb-3">
            <v-btn
              class="mt-3 text-center"
              text
              color="secondary"
              :disabled="dialogDynamicLink.loading"
              :loading="dialogDynamicLink.loading"
              @click="generateDynamicLink()"
            >
              <v-icon left dark color="primary"> mdi-link-plus </v-icon>
              Generate Dynamic Link
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { mapGetters } from "vuex";
import modalReauth from "@/components/admin/modal-reauth.vue";
import OptionsButton from "@/components/admin/OptionsButton.vue";
export default {
  name: "PreSetsList",
  components: { modalReauth, OptionsButton },
  data: () => ({
    editingName: {},
    editingDescription: {},
    originalName: {},
    originalDescription: {},
    dialogReAuth: {
      show: false,
      action: "",
      item: {},
    },
    dialog: {
      show: false,
      item: {},
    },
    dialogPreviewImage: {
      show: false,
      img: "",
    },
    snackbar: {
      snackbar: false,
      timeout: 2000,
      text: null,
      color: "",
    },
    headers: [
      { text: "", align: "center", value: "image", sortable: false },
      { text: "Name", align: "center", value: "name.ENG", sortable: true },
      {
        text: "Description",
        align: "center",
        value: "description.ENG",
        sortable: true,
      },
      {
        text: "Reproductions",
        align: "center",
        value: "completedTimes",
        sortable: true,
      },
      {
        text: "Avg MXP",
        align: "center",
        value: "avgMXP",
        sortable: true,
      },
      {
        text: "Avg Calories",
        align: "center",
        value: "avgCalories",
        sortable: true,
      },
      {
        text: "Link",
        align: "center",
        value: "dynamicLink",
        sortable: false,
      },
      { text: "Type", align: "center", value: "typeBy", sortable: true },
      { text: "Circuits", align: "center", value: "circuits", sortable: true },
      { text: "Enabled", align: "center", value: "enabled", sortable: true },
      {
        text: "Free",
        align: "center",
        value: "isFree",
        sortable: true,
      },
      {
        text: "Grandfathered",
        align: "center",
        value: "isGrandFathered",
        sortable: true,
      },
      { text: "Action", align: "center", value: "action", sortable: false },
    ],
    search: "",
    loading: false,
    duplicateLoading: false,
    page: 0,
    itemsPerPage: 10,
    sortBy: "name.ENG",
    dialogDynamicLink: {
      show: false,
      loading: false,
      item: {},
    },
  }),
  created() {
    let vm = this;
    vm.getAllPreSets();
    if (this.$route.params.previousRoute === "Pre Sets Edit") {
      const tableState = localStorage.getItem("preSetsTableState");
      if (tableState) {
        const tableStateObj = JSON.parse(tableState);
        this.search = tableStateObj.query;
        this.itemsPerPage = tableStateObj.hitsPerPage;
        this.page = tableStateObj.page;
      }
    } else {
      this.itemsPerPage = -1;
    }
  },
  methods: {
    async getAllPreSets() {
      const vm = this;
      try {
        vm.loading = true;
        await vm.$store.dispatch("GetPreSets");
        vm.loading = false;
      } catch (error) {
        vm.loading = false;
        console.log(error);
      }
    },
    changeRoutePreSet(preSet) {
      let vm = this;
      vm.$router
        .push({ name: "Pre Sets Edit", params: preSet })
        .catch((err) => {
          vm.snackbar.text =
            "Sorry, you don't have permissions to enter this view.";
          vm.snackbar.color = "red darken-2";
          vm.snackbar.snackbar = true;
        });
    },
    openDialogConfirm(item) {
      let vm = this;
      vm.dialog.show = true;
      vm.dialog.item = item;
    },
    closeDialogConfirm() {
      let vm = this;
      vm.dialog.show = false;
      vm.dialog.item = "";
    },
    isSuccessReAuth(dialogReAuthResponse) {
      const vm = this;
      switch (dialogReAuthResponse.action) {
        case "deletePreSet":
          vm.deletePreSet(dialogReAuthResponse.item);
          break;
        default:
          break;
      }
    },
    openDialogReAuth(action, item) {
      let vm = this;
      vm.dialogReAuth.show = true;
      vm.dialogReAuth.action = action;
      vm.dialogReAuth.item = item;
      vm.closeDialogConfirm();
    },
    openDialogPreviewImage(img) {
      const vm = this;
      vm.dialogPreviewImage = {
        show: true,
        img: img ? img : "",
      };
    },
    closeDialogImagePreview() {
      const vm = this;
      vm.dialogPreviewImage.show = false;
    },
    async duplicatePreSet(preSet) {
      const vm = this;
      try {
        vm.duplicateLoading = true;
        const newPreSet = await vm.$store.dispatch("CopyPreSet", preSet.id);
        await vm.$store.dispatch("GenerateLog", {
          id: newPreSet.id,
          name: newPreSet.name["ENG"],
          type: "createdPreSet",
        });
        vm.duplicateLoading = false;
        vm.getAllPreSets();
      } catch (error) {
        vm.duplicateLoading = false;
        let emitData = {
          snackbar: true,
          btn: "",
          y: "bottom",
          x: null,
          mode: "",
          timeout: 2000,
          text: "An error occurred duplicating the PreSet",
          color: "red",
        };
        vm.$emit("showSnackBar", emitData);
        console.log(error);
      }
    },
    async updateValue(preset, key, value) {
      const vm = this;

      // Store original value for rollback in case of error
      const originalValue = this.getNestedValue(preset, key);

      // Update local data immediately for better UX
      this.setNestedValue(preset, key, value);

      try {
        // Update database in background
        await vm.$store.dispatch("UpdatePresetValue", {
          id: preset.id,
          data: { [key]: value },
        });

        await vm.$store.dispatch("GenerateLog", {
          id: preset.id,
          title: preset.name["ENG"],
          type: "editedPreSet",
        });

        let emitData = {
          snackbar: true,
          btn: "",
          y: "bottom",
          x: null,
          mode: "",
          timeout: 2000,
          text: "Preset edited successfully",
          color: "success",
        };
        vm.$emit("showSnackBar", emitData);
      } catch (error) {
        // Rollback local changes if database update fails
        this.setNestedValue(preset, key, originalValue);

        console.error("Error updating preset:", error);

        let emitData = {
          snackbar: true,
          btn: "",
          y: "bottom",
          x: null,
          mode: "",
          timeout: 3000,
          text: "Error updating preset. Please try again.",
          color: "error",
        };
        vm.$emit("showSnackBar", emitData);
      }
    },
    async deletePreSet(preSet) {
      const vm = this;
      try {
        vm.$emit("showOverlay", true);
        await vm.$store.dispatch("DeletePreSet", preSet);
        await vm.$store.dispatch("GenerateLog", {
          id: preSet.id,
          name: preSet.name["ENG"],
          type: "deletedPreSet",
        });
        vm.getAllPreSets();
        vm.$emit("showOverlay", false);
      } catch (error) {
        vm.$emit("showOverlay", false);
        console.error(error);
        let emitData = {
          snackbar: true,
          btn: "",
          y: "bottom",
          x: null,
          mode: "",
          timeout: 2000,
          text: "Error deleting PreSet",
          color: "red",
        };
        vm.$emit("showSnackBar", emitData);
      }
    },
    changeRouteFilters() {
      let vm = this;
      vm.$router.push({ name: "Pre Sets Filters" }).catch((err) => {
        vm.snackbar.text =
          "Sorry, you don't have permissions to enter this view.";
        vm.snackbar.color = "red darken-2";
        vm.snackbar.snackbar = true;
      });
    },
    createPreSet() {
      let vm = this;
      vm.$router.push({ name: "Pre Sets Create" }).catch((err) => {
        vm.snackbar.text =
          "Sorry, you don't have permissions to enter this view.";
        vm.snackbar.color = "red darken-2";
        vm.snackbar.snackbar = true;
      });
    },
    onPageChange(page) {
      console.log(page);
    },
    onItemsPerPageUpdate(items) {
      console.log(items);
    },
    onSortByChange(sortBy, test) {
      console.log(sortBy, test);
    },
    openDialogDynamicLink(item) {
      const vm = this;
      vm.dialogDynamicLink = {
        show: true,
        loading: false,
        item,
      };
    },
    closeDialogDynamicLink() {
      const vm = this;
      vm.dialogDynamicLink = {
        show: false,
        loading: false,
        item: {},
      };
    },
    generateDynamicLink() {
      const vm = this;
      vm.dialogDynamicLink.loading = true;

      const payload = {
        routeName: "preSets",
        title: vm.dialogDynamicLink.item.name.ENG,
        id: vm.dialogDynamicLink.item.id,
        img: vm.dialogDynamicLink.item.image.link,
      };
      vm.$store
        .dispatch("GenerateDynamicLink", payload)
        .then((dynamicLinks) => {
          vm.dialogDynamicLink.loading = false;
          vm.dialogDynamicLink.item.dynamicLink = dynamicLinks.shortLink;

          vm.updateValue(
            vm.dialogDynamicLink.item,
            "dynamicLink",
            dynamicLinks.shortLink
          )
            .then((live) => {
              // vm.closeDialogDynamicLink()
            })
            .catch((errUpdate) => {
              vm.dialogDynamicLink.loading = false;
              console.error(errUpdate);
            });
        })
        .catch((err) => {
          vm.dialogDynamicLink.loading = false;
          console.error(err);
        });
    },
    copyLink(link) {
      const vm = this;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          vm.snackbar.text = "Copied !";
          vm.snackbar.color = "dark";
          vm.snackbar.snackbar = true;
        })
        .catch((err) => {
          vm.snackbar.text = "Error coping link !";
          vm.snackbar.color = "red";
          vm.snackbar.snackbar = true;
        });
    },
    getNestedValue(obj, path) {
      return path.split(".").reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    },
    setNestedValue(obj, path, value) {
      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce((current, key) => {
        if (!current[key]) {
          this.$set(current, key, {});
        }
        return current[key];
      }, obj);
      this.$set(target, lastKey, value);
    },
    handleEnterKey(event, item, fieldType) {
      // Don't save if Shift key is pressed (Shift+Enter)
      if (event.shiftKey) {
        return;
      }

      // Prevent default behavior and save
      event.preventDefault();

      if (fieldType === "name") {
        this.saveName(item);
      } else if (fieldType === "description") {
        this.saveDescription(item);
      }
    },
    startEditingName(item) {
      this.$set(this.editingName, item.id, item.name.ENG);
      // Store original value for potential rollback
      this.$set(this.originalName, item.id, item.name.ENG);
      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput[0]) {
          this.$refs.nameInput[0].focus();
        }
      });
    },
    startEditingDescription(item) {
      this.$set(this.editingDescription, item.id, item.description.ENG);
      // Store original value for potential rollback
      this.$set(this.originalDescription, item.id, item.description.ENG);
      this.$nextTick(() => {
        if (this.$refs.descriptionInput && this.$refs.descriptionInput[0]) {
          this.$refs.descriptionInput[0].focus();
        }
      });
    },
    async saveName(item) {
      const newName = this.editingName[item.id];
      if (newName && newName.trim() !== item.name.ENG) {
        try {
          await this.updateValue(item, "name.ENG", newName.trim());
        } catch (error) {
          console.error("Error saving name:", error);
        }
      }
      this.$delete(this.editingName, item.id);
      this.$delete(this.originalName, item.id);
    },
    async saveDescription(item) {
      const newDescription = this.editingDescription[item.id];
      if (newDescription !== item.description.ENG) {
        try {
          await this.updateValue(item, "description.ENG", newDescription);
        } catch (error) {
          console.error("Error saving description:", error);
        }
      }
      this.$delete(this.editingDescription, item.id);
      this.$delete(this.originalDescription, item.id);
    },
    cancelEditingName(item) {
      // Restore original value
      if (this.originalName[item.id] !== undefined) {
        this.$set(item.name, "ENG", this.originalName[item.id]);
        this.$delete(this.originalName, item.id);
      }
      this.$delete(this.editingName, item.id);
    },
    cancelEditingDescription(item) {
      // Restore original value
      if (this.originalDescription[item.id] !== undefined) {
        this.$set(item.description, "ENG", this.originalDescription[item.id]);
        this.$delete(this.originalDescription, item.id);
      }
      this.$delete(this.editingDescription, item.id);
    },
  },
  computed: {
    ...mapGetters(["getPreSets"]),
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === "Pre Sets Edit") {
      const currentState = JSON.stringify({
        hitsPerPage: this.itemsPerPage,
        query: this.search,
        page: this.page,
      });
      localStorage.setItem("preSetsTableState", currentState);
    } else {
      localStorage.removeItem("preSetsTableState");
    }
    next();
  },
  beforeRouteEnter(to, from, next) {
    if (from.name === "Pre Sets Edit") {
      to.params.previousRoute = from.name;
    }
    next();
  },
};
</script>
<style scoped>
.v-data-table /deep/ .sticky-header {
  position: sticky;
  top: var(--toolbarHeight);
}

.v-data-table /deep/ .v-data-table__wrapper {
  overflow: unset;
}
.descriptionSlot {
  max-width: 20vw;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
.nameSlot {
  max-width: 20vw;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.editable-field {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.editable-field:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 4px;
}

.editable-input {
  max-width: 20vw;
}
</style>